import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Stack,
  TextField,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import CartaoMetrica from './components/CartaoMetrica';
import GraficoFluxo from './components/GraficoFluxo';
import GraficoDespesasCategoria from './components/GraficoDespesasCategoria';
import AlertasCriticas from './components/AlertasCriticas';
import API_URL from '../../../config/api';

const RelatoriosFinanceiros = () => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  // Obter data do mês atual
  useEffect(() => {
    const hoje = new Date();
    const primeiro = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimo = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

    setDataInicio(primeiro.toISOString().split('T')[0]);
    setDataFim(ultimo.toISOString().split('T')[0]);
  }, []);

  // Buscar dados ao carregar
  useEffect(() => {
    if (dataInicio && dataFim) {
      carregarDados();
    }
  }, [dataInicio, dataFim]);

  const carregarDados = async () => {
    try {
      setCarregando(true);
      setErro(null);

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/relatorios-financeiros/saude-financeira`, {
        params: {
          dataInicio,
          dataFim
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setDados(response.data.dados);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setErro('Erro ao carregar dados financeiros. Tente novamente.');
    } finally {
      setCarregando(false);
    }
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(valor || 0);
  };

  const obterCor = (metrica) => {
    if (metrica === 'caixa') {
      if (dados?.caixa?.saldoLiquido > 10000) return 'success';
      if (dados?.caixa?.saldoLiquido > 5000) return 'warning';
      return 'error';
    }
    if (metrica === 'aReceber') {
      if (dados?.contasReceber?.totalPendente > 20000) return 'warning';
      return 'primary';
    }
    return 'primary';
  };

  const exportarPDF = () => {
    if (!dados) return;

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const dataAtual = new Date().toLocaleString('pt-BR');

    // ===== CABEÇALHO =====
    let yPos = 15;
    
    doc.setFillColor(31, 31, 31);
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    doc.setTextColor(247, 139, 0);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('EASY LOCADORA', pageWidth / 2, 12, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('RELATORIO DE SAUDE FINANCEIRA', pageWidth / 2, 20, { align: 'center' });

    yPos = 32;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em: ${dataAtual}`, pageWidth - 15, yPos, { align: 'right' });

    yPos = 40;

    // ===== PERÍODO =====
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(247, 139, 0);
    doc.text('PERIODO ANALISADO:', 15, yPos);
    
    yPos += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`${new Date(dataInicio).toLocaleDateString('pt-BR')} a ${new Date(dataFim).toLocaleDateString('pt-BR')}`, 15, yPos);

    yPos += 10;

    // ===== RESUMO FINANCEIRO =====
    doc.setFillColor(247, 139, 0);
    doc.rect(15, yPos - 5, pageWidth - 30, 8, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('RESUMO FINANCEIRO', pageWidth / 2, yPos, { align: 'center' });

    yPos += 10;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');

    const resultado = (dados?.receita?.totalReceita || 0) - (dados?.despesas?.totalDespesa || 0);

    // Métricas em 3 colunas
    const col1X = 15;
    const col2X = pageWidth / 3 + 10;
    const col3X = (pageWidth / 3) * 2 + 5;
    let currentY = yPos;

    // Coluna 1
    doc.setFont('helvetica', 'bold');
    doc.text('Caixa Disponivel:', col1X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarMoeda(dados?.caixa?.saldoLiquido || 0), col1X + 45, currentY);
    currentY += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Receita Total:', col1X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarMoeda(dados?.receita?.totalReceita || 0), col1X + 45, currentY);

    // Coluna 2
    currentY = yPos;
    doc.setFont('helvetica', 'bold');
    doc.text('Despesa Total:', col2X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarMoeda(dados?.despesas?.totalDespesa || 0), col2X + 45, currentY);
    currentY += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Resultado (Lucro):', col2X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(resultado >= 0 ? 0 : 255, resultado >= 0 ? 128 : 0, 0);
    doc.text(formatarMoeda(resultado), col2X + 45, currentY);
    doc.setTextColor(0, 0, 0);

    // Coluna 3
    currentY = yPos;
    doc.setFont('helvetica', 'bold');
    doc.text('A Receber:', col3X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarMoeda(dados?.contasReceber?.totalPendente || 0), col3X + 35, currentY);
    currentY += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('A Pagar:', col3X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarMoeda(dados?.contasPagar?.totalPendente || 0), col3X + 35, currentY);

    yPos += 15;

    // ===== DESPESAS POR CATEGORIA =====
    if (dados?.graficos?.despesasCategoria && dados.graficos.despesasCategoria.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(247, 139, 0);
      doc.text('DESPESAS POR CATEGORIA:', 15, yPos);
      yPos += 2;

      const tableColumns = ['Categoria', 'Valor', 'Percentual'];
      const totalDespesas = dados.graficos.despesasCategoria.reduce((sum, cat) => sum + (cat.valor || 0), 0);
      const tableData = dados.graficos.despesasCategoria.map(cat => [
        cat.categoria,
        formatarMoeda(cat.valor || 0),
        `${((cat.valor / totalDespesas) * 100).toFixed(1)}%`
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [tableColumns],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [247, 139, 0],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 2
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { left: 15, right: 15 },
        columnStyles: {
          0: { cellWidth: 100 },
          1: { halign: 'right', cellWidth: 50 },
          2: { halign: 'center', cellWidth: 40 }
        }
      });

      yPos = doc.lastAutoTable.finalY + 10;
    }

    // ===== ALERTAS =====
    if (dados?.alertas?.alugueisAtrasados?.length > 0) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(211, 47, 47);
      doc.text('ALERTAS CRITICOS:', 15, yPos);
      yPos += 6;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(`Alugueis Atrasados: ${dados.alertas.alugueisAtrasados.length}`, 15, yPos);
      yPos += 5;

      if (dados?.alertas?.fornecedoresVencendo?.length > 0) {
        doc.text(`Contas a Pagar Vencendo: ${dados.alertas.fornecedoresVencendo.length}`, 15, yPos);
      }
    }

    // Rodapé
    if (yPos < pageHeight - 15) {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `Relatorio gerado automaticamente pelo sistema Easy Locadora - ${dataAtual}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    const dataArquivo = new Date().toISOString().split('T')[0];
    doc.save(`relatorio-saude-financeira-${dataArquivo}.pdf`);
  };

  const exportarExcel = () => {
    if (!dados) return;

    const dataAtual = new Date().toLocaleString('pt-BR');

    // ===== CABEÇALHO =====
    const cabecalho = [
      ['EASY LOCADORA'],
      ['RELATORIO DE SAUDE FINANCEIRA'],
      [`Gerado em: ${dataAtual}`],
      [`Periodo: ${new Date(dataInicio).toLocaleDateString('pt-BR')} a ${new Date(dataFim).toLocaleDateString('pt-BR')}`],
      [''],
    ];

    const resultado = (dados?.receita?.totalReceita || 0) - (dados?.despesas?.totalDespesa || 0);

    // ===== RESUMO FINANCEIRO =====
    const resumo = [
      ['RESUMO FINANCEIRO'],
      ['Caixa Disponivel:', formatarMoeda(dados?.caixa?.saldoLiquido || 0)],
      ['Receita Total:', formatarMoeda(dados?.receita?.totalReceita || 0)],
      ['Despesa Total:', formatarMoeda(dados?.despesas?.totalDespesa || 0)],
      ['Resultado (Lucro):', formatarMoeda(resultado)],
      ['A Receber (Clientes):', formatarMoeda(dados?.contasReceber?.totalPendente || 0)],
      ['A Pagar (Fornecedores):', formatarMoeda(dados?.contasPagar?.totalPendente || 0)],
      [''],
    ];

    // ===== DESPESAS POR CATEGORIA =====
    const despesasHeader = ['Categoria', 'Valor', 'Percentual'];
    const totalDespesas = dados?.graficos?.despesasCategoria?.reduce((sum, cat) => sum + (cat.valor || 0), 0) || 0;
    const despesasBody = dados?.graficos?.despesasCategoria?.map(cat => [
      cat.categoria,
      formatarMoeda(cat.valor || 0),
      `${((cat.valor / totalDespesas) * 100).toFixed(1)}%`
    ]) || [];

    const tabelaDespesas = [
      ['DESPESAS POR CATEGORIA:'],
      despesasHeader,
      ...despesasBody,
      [''],
    ];

    // ===== ALERTAS =====
    const alertas = [['ALERTAS:']];
    if (dados?.alertas?.alugueisAtrasados?.length > 0) {
      alertas.push(['Alugueis Atrasados:', dados.alertas.alugueisAtrasados.length]);
    }
    if (dados?.alertas?.fornecedoresVencendo?.length > 0) {
      alertas.push(['Contas a Pagar Vencendo:', dados.alertas.fornecedoresVencendo.length]);
    }
    alertas.push(['']);

    // ===== MONTAGEM FINAL =====
    const dadosCompletos = [
      ...cabecalho,
      ...resumo,
      ...tabelaDespesas,
      ...alertas
    ];

    const ws = XLSX.utils.aoa_to_sheet(dadosCompletos);

    // Larguras das colunas
    ws['!cols'] = [
      { wch: 30 },
      { wch: 20 },
      { wch: 15 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Saude Financeira');

    const dataArquivo = new Date().toISOString().split('T')[0];
    const nomeArquivo = `relatorio-saude-financeira-${dataArquivo}.xlsx`;
    const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buffer]), nomeArquivo);
  };

  if (!dataInicio) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3, px: { xs: 1, sm: 2 } }}>
      {/* HEADER */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #1f1f1f 0%, #f78b00 100%)',
          p: 3,
          borderRadius: 1,
          color: 'white',
          mb: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <AssessmentIcon sx={{ fontSize: 32 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              💹 SAÚDE FINANCEIRA DA EMPRESA
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Acompanhe a saúde financeira do seu negócio em tempo real
          </Typography>
        </Box>

        {/* FILTROS */}
        <Box sx={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #e0e0e0',
          p: 2,
          borderRadius: 1
        }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, width: { xs: '100%', sm: 'auto' } }}>
              <FilterAltIcon sx={{ color: '#f78b00', fontSize: 24 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Período:
              </Typography>
            </Box>
            <TextField
              label="Data Início"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                backgroundColor: 'white',
                '& .MuiOutlinedInput-root': {
                  '&:focus-within fieldset': {
                    borderColor: '#f78b00',
                    borderWidth: 2
                  }
                }
              }}
            />
            <TextField
              label="Data Fim"
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                backgroundColor: 'white',
                '& .MuiOutlinedInput-root': {
                  '&:focus-within fieldset': {
                    borderColor: '#f78b00',
                    borderWidth: 2
                  }
                }
              }}
            />
            <Button
              variant="contained"
              onClick={carregarDados}
              disabled={carregando}
              sx={{
                background: 'linear-gradient(135deg, #1f1f1f 0%, #f78b00 100%)',
                textTransform: 'none',
                fontWeight: 'bold',
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {carregando ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Atualizar'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<PictureAsPdfIcon />}
              onClick={exportarPDF}
              disabled={carregando || !dados}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Exportar PDF
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<TableChartIcon />}
              onClick={exportarExcel}
              disabled={carregando || !dados}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Exportar Excel
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ERRO */}
      {erro && <Alert severity="error" sx={{ mb: 3 }}>{erro}</Alert>}

      {/* CARREGANDO */}
      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : dados ? (
        <>
          {/* SEÇÃO 1: 6 MÉTRICAS PRINCIPAIS */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1f1f1f' }}>
            📊 RESUMO RÁPIDO
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <CartaoMetrica
                titulo="💵 Caixa Disponível"
                valor={dados?.caixa?.saldoLiquido || 0}
                variacao={dados?.variacao?.caixa || 0}
                cor={obterCor('caixa')}
                icon={AccountBalanceWalletIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CartaoMetrica
                titulo="📈 Receita Total"
                valor={dados?.receita?.totalReceita || 0}
                variacao={dados?.variacao?.receita || 0}
                cor="primary"
                icon={TrendingUpIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CartaoMetrica
                titulo="📉 Despesa Total"
                valor={dados?.despesas?.totalDespesa || 0}
                variacao={dados?.variacao?.despesa || 0}
                cor="primary"
                icon={TrendingDownIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CartaoMetrica
                titulo="✅ Resultado (Lucro)"
                valor={(dados?.receita?.totalReceita || 0) - (dados?.despesas?.totalDespesa || 0)}
                variacao={dados?.variacao?.resultado || 0}
                cor="success"
                icon={TrendingUpIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CartaoMetrica
                titulo="💳 A Receber (Clientes)"
                valor={dados?.contasReceber?.totalPendente || 0}
                variacao={dados?.variacao?.aReceber || 0}
                cor={obterCor('aReceber')}
                icon={AccountBalanceWalletIcon}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CartaoMetrica
                titulo="📋 A Pagar (Fornecedores)"
                valor={dados?.contasPagar?.totalPendente || 0}
                variacao={dados?.variacao?.aPagar || 0}
                cor="primary"
                icon={AccountBalanceWalletIcon}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* SEÇÃO 2: GRÁFICOS */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1f1f1f' }}>
            📈 ANÁLISE VISUAL
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} lg={6}>
              <GraficoFluxo
                dados={dados?.graficos?.fluxo30Dias || []}
                periodo="30dias"
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <GraficoFluxo
                dados={dados?.graficos?.fluxo12Meses || []}
                periodo="12meses"
              />
            </Grid>
            <Grid item xs={12}>
              <GraficoDespesasCategoria
                dados={dados?.graficos?.despesasCategoria || []}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* SEÇÃO 3: ALERTAS CRÍTICAS */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1f1f1f' }}>
            🔴 ALERTAS E ATENÇÃO
          </Typography>
          <AlertasCriticas
            alugueisAtrasados={dados?.alertas?.alugueisAtrasados || []}
            fornecedoresVencendo={dados?.alertas?.fornecedoresVencendo || []}
          />

          {/* RODAPÉ */}
          <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#999' }}>
              ✓ Dados atualizados em tempo real | Período: {new Date(dataInicio).toLocaleDateString('pt-BR')} a {new Date(dataFim).toLocaleDateString('pt-BR')}
            </Typography>
          </Box>
        </>
      ) : null}
    </Container>
  );
};

export default RelatoriosFinanceiros;
